import { DateTime } from "luxon";
import PhoneAppointment from "models/phone-appointment";
import { ScreeningFlow } from "models/screening-flow";

const ENDPOINTS = {
    LOGIN: "/auth/login",
    SIGNUP: "/users",
    RESEND_CONFIRMATION: "/auth/email/resend",
    CONFIRM_EMAIL: "/auth/email",
    REQUEST_TFA_CODE: "/auth/tfa/resend",
    VERIFY_TFA: "/auth/tfa/validate",
    REQUEST_RESET_PASSWORD_EMAIL: "/auth/password/email",
    RESET_PASSWORD: "/auth/password/reset",
    VALIDATE_RESET_PASSWORD_TOKEN: "/auth/reset-token/validate",
    REQUEST_PHONE_CONFIRMATION_CODE: "/auth/phone/send",
    CONFIRM_PHONE_NUMBER: "/auth/phone/confirm",
    SET_TFA_SETTINGS: "/auth/tfa/setup",
    UNSUBSCRIBE: (token: string): string => `/unsubscribe?token=${token}`,
    QUESTIONNAIRE: (screeningFlow: ScreeningFlow | number): string =>
        `/questionnaires/${screeningFlow}`,
    ME: "/users/me",
    CARD_INFO: "/users/me/card",
    APPOINTMENTS: "/appointments",
    APPOINTMENT: (id: number): string => `/appointments/${id}`,
    APPOINTMENT_AVAILABLE_DATES: (
        year: number,
        month: number,
        clinicId: number | undefined,
        usesPrivateLab: boolean | undefined
    ): string => {
        const labType = usesPrivateLab ? "private" : "public";
        const startDate = DateTime.fromObject({
            year,
            month: month + 1,
            day: 1,
        }).toISODate();
        const endDate = DateTime.fromObject({ year, month: month + 1 })
            .endOf("month")
            .toISODate();

        const params = new URLSearchParams([
            ["start", startDate],
            ["end", endDate],
            ...(usesPrivateLab === undefined ? [] : [["labType", labType]]),
        ]);

        return `/clinic/${clinicId}/available-dates?${params.toString()}`;
    },
    APPOINTMENT_AVAILABLE_TIMES: (
        date: Date,
        clinicId: number | undefined,
        usesPrivateLab: boolean | undefined
    ): string => {
        const isoDate = DateTime.fromJSDate(date).toISODate();
        const labType = usesPrivateLab ? "private" : "public";

        const params = new URLSearchParams([
            ["date", isoDate],
            ...(usesPrivateLab === undefined ? [] : [["labType", labType]]),
        ]);

        return `/clinic/${clinicId}/available-start-times?${params.toString()}`;
    },
    APPOINTMENT_SET_PRIVATE_LAB: (id: number): string =>
        `/appointments/${id}/set-uses-private-lab`,
    APPOINTMENT_RESERVE: "/appointments/reserve",
    PHONE_APPOINTMENT_AVAILABLE_DATE: (
        year: number,
        month: number,
        { id }: PhoneAppointment
    ): string =>
        `/schedule-entry/available-dates?type=phone-appointment&year=${year}&month=${month}&phoneAppointmentId=${id}`,
    PHONE_APPOINTMENT_AVAILABLE_TIME: (
        date: Date,
        { id }: PhoneAppointment
    ): string => {
        const isoDate = DateTime.fromJSDate(date).toISODate();
        return `/schedule-entry/available-start-times?type=phone-appointment&date=${isoDate}&phoneAppointmentId=${id}`;
    },
    PHARMACIST_APPOINTMENT_AVAILABLE_DATE: (
        year: number,
        month: number,
        phoneAppointmentId?: number | undefined
    ): string =>
        phoneAppointmentId != null
            ? `/schedule-entry/available-dates?type=pharmacist-appointment&year=${year}&month=${month}&phoneAppointmentId=${phoneAppointmentId}`
            : `/schedule-entry/available-dates?type=pharmacist-appointment&year=${year}&month=${month}`,
    PHARMACIST_APPOINTMENT_AVAILABLE_TIME: (
        date: Date,
        phoneAppointmentId?: number | undefined
    ): string => {
        const isoDate = DateTime.fromJSDate(date).toISODate();
        return phoneAppointmentId != null
            ? `/schedule-entry/available-start-times?type=pharmacist-appointment&date=${isoDate}&phoneAppointmentId=${phoneAppointmentId}`
            : `/schedule-entry/available-start-times?type=pharmacist-appointment&date=${isoDate}`;
    },
    USER_SATISFACTION_QUESTIONNAIRE: "/user-satisfaction-questionnaires",
    GET_USER_PUBLIC_REVIEWS: "/users/me/reviews/preferences/clinics",
    UPDATE_USER_PUBLIC_REVIEWS: (clinicId: string): string =>
        `/users/me/reviews/preferences/clinics/${clinicId}`,
    BOOK_RETAKE_APPOINTMENT: "/appointments/retake",
    REJECT_APPOINTMENT_RETAKE: (id: number): string =>
        `/appointments/${id}/reject-retake`,
    SCHEDULE_PHONE_APPOINTMENT: (id: number): string =>
        `/phone-appointments/${id}`,
    REJECT_PHONE_APPOINTMENT: (id: number): string =>
        `/phone-appointments/${id}/reject`,
    VALIDATE_COUPON: (couponName: string): string => `/coupons/${couponName}`,
    USER_QUESTIONNAIRE: "user-questionnaires",
    USER_PAYMENT_INFO: "/users/me/payment-info",
    MEDICAL_TESTS_RECOMMENDATIONS: (userQuestionnaireId: number): string =>
        `/user-questionnaires/${userQuestionnaireId}/medical-tests`,
    UPDATE_APPOINTMENT_TESTS: (id: number): string =>
        `/appointments/${id}/chosen-medical-tests`,
    RESCHEDULE_APPPOINTEMENT_FEES: (id: number): string =>
        `/appointments/${id}/with-fees`,
    CLINICS: "/clinic",
    PAY_MISSED_APPPOINTEMENT_FEES: (id: number): string =>
        `/appointments/${id}/missed-appointment-fees`,
    DELIVERY: "/delivery",
    DELIVERY_SETTINGS: "/delivery-settings",
    REVOKE_PREP_STATUS: "/prep/status",
};

export const BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:4000/api";

export default ENDPOINTS;
